import { sxDarkGreyBody, sxFormError, sxTableTitle } from "../../../assets/styles/CommonSxProps";
import { Box, InputAdornment, MenuItem, OutlinedInput, Paper, Select, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DefaultButton from "../../../components/Accessibility/DefaultButton";
import PageTitle from "../../../components/Accessibility/PageTitle";
import TypographyAccessibility from "../../../components/Accessibility/TypographyAccessibility";
import VoltarButton from "../../../components/Accessibility/VoltarButton";
import WhiteContainer from "../../../components/Accessibility/WhiteContainer";
import LoadingApp from "../../../components/LoadingApp";
import ModalFeedBack from "../../../components/ModalFeedBack";
import { HistOcoOuvidor } from "../../../interfaces/entities/HistOcoOuvidor";
import { SolicitacaoReanalise } from "../../../interfaces/entities/ReanaliseNegativa/SolicitacaoReanalise";
import { Anexo } from "../../../interfaces/entities/Reembolso/Anexo";
import { ReanaliseNegativaValidation } from "../../../interfaces/validation/Contrato/ReanaliseNegativaValidation";
import { getHistOcorOuvidor, solicitarReanaliseNegativa } from "../../../services/ContratoService";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import { getBenef } from "../../../Store/storages/BeneficiarioStorage";
import { onlyNumbers } from "../../../utils/OnlyNumbers";
import { sxLabel } from "../MinhasGuias/sxProps";
import './styles.css';
import { sxInputSmall, sxObservationTextField, sxTableMaxWidth } from "./sxProps";
import AnexoButton from "../../../components/AnexoButton";
import { openCamilaChat } from "../../../utils/CamilaUtils";

const ReanaliseDeNegativa: React.FC = () => {

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Unimed-Sans',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    border: 'none',
    width: '25%',
    backgroundColor: 'inherit',
    ...accessibility.textStyleAccessibility
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: '#F9F9F9',
      border: 'none',
    }
  }));

  const accessibility = useAccessibility();
  const navigate = useNavigate();
  const benef = getBenef();
  const mobileScreen = useMediaQuery('(max-width: 768px)');
  const [fillingForm, setFillingForm] = useState<boolean>(false);
  const [historicoReanalise, setHistoricoReanalise] = useState<HistOcoOuvidor[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [anexos, setAnexos] = useState<Array<Anexo>>([]);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);

  const openModal = (
    icon: 'success' | 'warning' | 'error', title: string, subTitle: string, textButton: string, closeAction: () => void, buttonAction: () => void
  ) => {
    setModalFeedback({
      icon,
      title,
      subTitle,
      textButton,
      showButtonModal: true,
      closeAction: closeAction,
      buttonAction: buttonAction
    });
  }

  const formik = useFormik({
    initialValues: {
      observacao: '',
      numSolicitacao: '',
    },
    validationSchema: ReanaliseNegativaValidation,
    onSubmit: (values) => {
      sendSolicitation({ observacao: values.observacao, numSolicitacao: values.numSolicitacao, file: anexos[0] });
    },
    validateOnChange: false
  });

  useEffect(() => {
    if (!fillingForm) fetchHistorico();
    else resetAndLoadForm();
  }, [fillingForm]);

  const resetAndLoadForm = async () => {
    formik.resetForm();
    setAnexos([]);
  }

  const goToStart = () => {
    toggleFillingForm();
    closeModal();
  }

  const sendSolicitation = async (solicitacaoReanalise: SolicitacaoReanalise) => {
    setLoading(true);
    try {
      const response = await solicitarReanaliseNegativa(solicitacaoReanalise);
      if (response?.status === 200) {
        const message: string = response.data.mensagemCliente;
        if (message && message.includes('existe protocolo registrado')) {
          openModal('warning', 'Atenção', message, 'Ver histórico', goToStart, goToStart);
        } else {
          const successMessage = response.data.mensagemCliente;
          openModal('success', 'Sucesso', successMessage, 'Ver histórico', goToStart, goToStart);
        }
        formik.resetForm();
        setAnexos([]);
      } else {
        const errorMessage = response?.data?.mensagem ? response.data.mensagem : 'Ocorreu um erro ao solicitar a reanálise de negativa.';
        openModal('error', 'Erro', errorMessage, 'Fechar', closeModal, closeModal);
      }
    } catch (error) {
      console.error(error);
      openModal('error', 'Erro', 'Ocorreu um erro ao solicitar a reanálise de negativa.', 'Fechar', closeModal, closeModal);
    } finally {
      setLoading(false);
    }
  }

  const fetchHistorico = async () => {
    setLoading(true);
    const response = await getHistOcorOuvidor();
    if (response?.status === 200) {
      setHistoricoReanalise(response.data.lstHistoricoOcoOuvidor);
    } else {
      console.error(response);
      openModal('error', 'Erro', 'Ocorreu um erro ao consultar o histórico de solicitações de reanálise de negativa.', 'Fechar', closeModal, closeModal);
    }
    setLoading(false);
  }

  const allFieldsFilled = Object.values(formik.values).every(value => value !== '');

  const closeModal = () => {
    setModalFeedback(null);
  }

  const toggleFillingForm = () => {
    setFillingForm(!fillingForm);
  }

  return (
    <>
      <LoadingApp showLoading={loading} />
      {fillingForm ?
        <form className="feature-container" onSubmit={formik.handleSubmit} noValidate>
          <PageTitle text="Reanálise de negativa" />
          <TypographyAccessibility sx={sxDarkGreyBody} maxWidth={'650px'}>
            Solicite aqui a reanálise da sua guia negada, anexando um relatório médico detalhado e os laudos dos exames nos formatos <b>.pdf</b>, <b>.jpg</b> ou <b>.png</b>, com limite de <b>5MB</b> por arquivo.
          </TypographyAccessibility>
          <TypographyAccessibility sx={sxDarkGreyBody} maxWidth={'650px'}>
          <b>Importante:</b> certifique-se de preencher corretamente os dados do beneficiário cuja guia não foi autorizada, pois a solicitação não pode ser feita por terceiros. 
          </TypographyAccessibility>
          <TypographyAccessibility sx={sxDarkGreyBody} maxWidth={'650px'}>
            O prazo para resposta da reanálise pela Ouvidoria não será superior a sete dias úteis, contados da data de seu requerimento.
          </TypographyAccessibility>
          <WhiteContainer className="white-advice-container" >
            <TypographyAccessibility sx={sxDarkGreyBody}>
              O acompanhamento de sua solicitação poderá ser feito por meio virtual, pelo canal do cliente na aba reanálise,&nbsp; 
              <strong className="central-relacionamento-link" onClick={() => openCamilaChat()} tabIndex={0}>
              chat
              </strong>, ou presencial através da Central de Atendimento ou contato com o Teleatendimento/ CRMC – Central de Relacionamento Multicanais.
            </TypographyAccessibility>
          </WhiteContainer>

          <TypographyAccessibility sx={sxDarkGreyBody}><b>Nome do cliente: </b> {benef?.nome + ' ' + benef?.sobrenome}</TypographyAccessibility>
          <div className='input-holder width-100'>
            <TypographyAccessibility sx={sxLabel}>Observação</TypographyAccessibility>
            <TextField
              id="outlined-textarea"
              type='text'
              value={formik.values.observacao}
              onChange={(e) => {
                formik.setFieldValue('observacao', e.target.value);
              }}
              placeholder='Digite aqui as observações'
              multiline
              inputProps={{ maxLength: 500 }}
              sx={{
                ...sxObservationTextField,
                "& .MuiInputAdornment-root": {
                  position: "absolute",
                  bottom: 12,
                  right: 8,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="textSecondary">
                      {formik.values.observacao.length} / 500
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
            <TypographyAccessibility sx={sxFormError}>{formik.errors.observacao}</TypographyAccessibility>
          </div>
          <div className='input-holder'>
            <Box display={'flex'}>
              <TypographyAccessibility sx={sxLabel}>Número da guia negada&nbsp;</TypographyAccessibility>
              <TypographyAccessibility sx={{ ...sxDarkGreyBody, fontStyle: 'italic' }}>(sem o dígito)</TypographyAccessibility>
            </Box>
            <OutlinedInput
              placeholder='Digite o número da guia negada'
              type={'number'}
              fullWidth={mobileScreen}
              value={formik.values.numSolicitacao}
              onChange={(e) => { formik.setFieldValue('numSolicitacao', onlyNumbers(e.target.value)); }}
              name='numSolicitacao'
              error={!!formik.errors.numSolicitacao}
              inputProps={{ maxLength: 10 }}
              maxRows={1}
              sx={sxInputSmall}
            />
            <TypographyAccessibility sx={sxFormError}>{formik.errors.numSolicitacao}</TypographyAccessibility>
          </div>
          <div className="anexo-negativa-div">
            <TypographyAccessibility sx={sxDarkGreyBody} maxWidth={'650px'}>
              Anexe exames ou documentos na solicitação:
            </TypographyAccessibility>
            <div className="anexo-negativa-file-button">
              <AnexoButton anexos={anexos} salvarAnexos={anexos => { setAnexos(anexos) }} maxQtd={1} />
            </div>
        </div>
          <DefaultButton text="Solicitar" type="submit" disabled={!allFieldsFilled} />
          <VoltarButton onClick={toggleFillingForm} />
        </form>
        :
        <div className="feature-container">
          <PageTitle text="Reanálise de negativa" />
          <DefaultButton text="Nova solicitação" type="button" onClick={toggleFillingForm} />
          {historicoReanalise.length > 0 && 
          <TableContainer style={accessibility.contrastBorderAccessibility} className='container-table-result-exam' sx={sxTableMaxWidth} component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <StyledTableRow sx={{ width: '100%' }}>
                  <StyledTableCell>
                    <TypographyAccessibility sx={sxTableTitle}>Histórico de Solicitações</TypographyAccessibility>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell component="th" scope="row" align="left" sx={{ backgroundColor: '#F9F9F9' }}>
                    <b>Protocolo ANS</b>
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row" sx={{ backgroundColor: '#F9F9F9' }}>
                    <b>Data da solicitação</b>
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row" align="left" sx={{ backgroundColor: '#F9F9F9' }}>
                    <b>Status</b>
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {historicoReanalise.map((dados, index) => {
                  return (
                    <StyledTableRow
                      key={index}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <StyledTableCell component="th" scope="row">
                        {dados.cod_protoc_ans}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {dados.dt_ult_atualizacao}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {dados.descr}
                      </StyledTableCell>
                    </StyledTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>}
        </div>
      }
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </>
  )
}

export default ReanaliseDeNegativa;